import { architectureDisplayName } from '@/components/jobArchitecture/helpers';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';
import { getJobNavLabels } from '@/helpers/jobNavLabels';

export const canShowJobFamily = ({ isHrisProfile, hrisReferenceUlid }) => {
  if (isHrisProfile) return true;

  return Boolean(hrisReferenceUlid);
};

const toCompareSelectionItem = (ulid, title) => {
  const resolvedTitle = title || ulid;

  return {
    ulid,
    title: resolvedTitle,
    // Checkboxes show option.label, so keep it in sync with the title.
    label: resolvedTitle,
  };
};

export const getDefaultCompareSelection = ({ isHrisProfile, jobProfile }) => {
  if (!jobProfile?.ulid) {
    return { draftProfiles: [], hrisProfiles: [] };
  }

  const item = toCompareSelectionItem(jobProfile.ulid, jobProfile.title);

  if (isHrisProfile) {
    const draftProfiles = (jobProfile.draft_profiles || [])
      .filter((draft) => draft?.ulid)
      .map((draft) => toCompareSelectionItem(draft.ulid, draft.title));

    return { draftProfiles, hrisProfiles: [item] };
  }

  const hrisProfiles = [];
  const hrisUlid = jobProfile.hris_reference_ulid;

  if (hrisUlid) {
    hrisProfiles.push(
      toCompareSelectionItem(hrisUlid, jobProfile.hris_reference?.title),
    );
  }

  return { draftProfiles: [item], hrisProfiles };
};

// Add the draft's linked HRIS to the current HRIS chips without clearing extras.
export const mergeLinkedHrisIntoSelection = ({ hrisProfiles = [], jobProfile }) => {
  const hrisUlid = jobProfile?.hris_reference_ulid;

  if (!hrisUlid) {
    return hrisProfiles;
  }

  if (hrisProfiles.some((profile) => profile.ulid === hrisUlid)) {
    return hrisProfiles;
  }

  return [
    ...hrisProfiles,
    toCompareSelectionItem(hrisUlid, jobProfile?.hris_reference?.title),
  ];
};

export const buildCompareLaunchQuery = ({
  draftProfiles,
  hrisProfiles,
  from,
  fromId,
}) => {
  const query = { from, fromId };

  const draftUlids = (draftProfiles || []).map((profile) => profile.ulid).filter(Boolean);
  const hrisUlids = (hrisProfiles || []).map((profile) => profile.ulid).filter(Boolean);

  if (draftUlids.length) {
    query.draftProfiles = draftUlids.join(',');
  }

  if (hrisUlids.length) {
    query.profiles = hrisUlids.join(',');
  }

  return query;
};

export const familyKey = (group, family) => (
  `${group?.ulid || 'uncategorized'}:${family?.ulid || 'uncategorized'}`
);

export const familyLabel = (group, family) => (
  `${architectureDisplayName(group?.name)} > ${architectureDisplayName(family?.name)}`
);

export const listArchitectureFamilies = (treeData) => {
  const result = [];

  (treeData?.groups || []).forEach((group) => {
    (group.families || []).forEach((family) => {
      result.push({
        key: familyKey(group, family),
        label: familyLabel(group, family),
        group,
        family,
        profiles: family.profiles || [],
      });
    });
  });

  return result;
};

export const findFamilyForProfileUlid = (treeData, profileUlid) => {
  if (!profileUlid) return null;

  const families = listArchitectureFamilies(treeData);

  return families.find((entry) => (
    (entry.profiles || []).some((profile) => profile.ulid === profileUlid)
  )) || null;
};

export const filterHrisProfilesFromTree = (treeData, searchText, excludeUlids = []) => {
  const query = (searchText || '').trim().toLowerCase();
  const excluded = new Set(excludeUlids || []);

  if (!query) return [];

  const results = [];
  const seen = new Set();

  (treeData?.groups || []).forEach((group) => {
    (group.families || []).forEach((family) => {
      (family.profiles || []).forEach((profile) => {
        if (!profile.ulid || excluded.has(profile.ulid) || seen.has(profile.ulid)) {
          return;
        }

        const titleMatch = (profile.title || '').toLowerCase().includes(query);
        const codeMatch = (profile.hris_job_code || '').toLowerCase().includes(query);

        if (!titleMatch && !codeMatch) return;

        seen.add(profile.ulid);
        const title = profile.title || profile.ulid;

        results.push({
          ulid: profile.ulid,
          title,
          // Checkboxes show option.label, so keep it in sync with the title.
          label: title,
          jobLevelLabel: formatJobLevel(profile.job_level, { mode: 'combined' }),
        });
      });
    });
  });

  return results;
};

export const mapFamilyProfilesForList = (profiles = []) => (
  profiles.map((profile) => ({
    ulid: profile.ulid,
    title: profile.title || profile.ulid,
    jobLevelLabel: formatJobLevel(profile.job_level, { mode: 'short' }),
    jobLevelTooltip: formatJobLevel(profile.job_level, { mode: 'long' }),
  })).filter((profile) => profile.ulid)
);

// Linked profiles for the one open in the editor (not that profile itself).
export const getEditorLinkedProfileUlids = ({ isHrisProfile, jobProfile }) => {
  if (!jobProfile) return [];

  if (isHrisProfile) {
    return (jobProfile.draft_profiles || [])
      .map((draft) => draft?.ulid)
      .filter(Boolean);
  }

  return jobProfile.hris_reference_ulid ? [jobProfile.hris_reference_ulid] : [];
};

export const getEditorLinkedProfileTooltip = ({ isHrisProfile }) => {
  const labels = getJobNavLabels();

  if (isHrisProfile) {
    return labels.editorLinkedTooltipHris;
  }

  return labels.editorLinkedTooltipDraft;
};
